/**
 * RepDetail.tsx — One AE's truth screen.
 * Wired to live Supabase data.
 *
 * Route: /reporting/rep/:id   (id = users.id of the AE)
 *
 * NOTE TO BRYAN: filename intentionally kept as RepDetail.tsx for route stability.
 * TODO(rename-file): rename to AeDetail.tsx + update App.tsx import in a follow-up commit.
 *
 * All user-facing copy uses "AE" / "her" — never "rep" / "him" — per the brand rules.
 *
 * Sections:
 *   - Header (name, email, manager, quota, attainment)
 *   - Pipeline summary (commit / probable / upside / omitted / won values)
 *   - Account list (the AE's book of business, with bucket + CP green-count + days stale)
 *   - Recent activity (hunt_items across the AE's accounts)
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  AccountRow,
  ForecastRow,
  HuntItemRow,
  PipelineSummaryRow,
  SalesPlanRow,
  UserRow,
  bucketColor,
  daysSince,
  fmtUSD,
  listAccountForecasts,
  listAccounts,
  listAllHunt,
  listAllUsers,
  listCpStatusFor,
  listPipelineSummary,
  listSalesPlans,
  summarizeCpStatus,
} from "@/lib/reporting-live";

interface RepDetailProps {
  repId?: string;
}

interface State {
  loading: boolean;
  error: string | null;
  ae: UserRow | null;
  manager: UserRow | null;
  pipeline: PipelineSummaryRow | null;
  plan: SalesPlanRow | null;
  accounts: AccountRow[];
  forecasts: ForecastRow[];
  cpByAccount: ReturnType<typeof summarizeCpStatus> | null;
  hunt: HuntItemRow[];
}

const EMPTY: State = {
  loading: true,
  error: null,
  ae: null,
  manager: null,
  pipeline: null,
  plan: null,
  accounts: [],
  forecasts: [],
  cpByAccount: null,
  hunt: [],
};

export default function RepDetail({ repId }: RepDetailProps) {
  const [, navigate] = useLocation();
  const [state, setState] = useState<State>(EMPTY);
  // repId is a UUID string — do NOT parseInt (would produce NaN).
  const aeId = decodeURIComponent(repId || "").trim();

  useEffect(() => {
    if (!aeId) {
      setState({ ...EMPTY, loading: false, error: "No AE id supplied." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [users, pipeline, plans, allAccounts, forecasts] = await Promise.all([
          listAllUsers(),
          listPipelineSummary(),
          listSalesPlans(),
          listAccounts(),
          listAccountForecasts(),
        ]);
        if (cancelled) return;
        // Match by UUID string (users.id is a Supabase auth UUID).
        const ae = users.find((u) => String(u.id) === aeId) || null;
        if (!ae) {
          setState({ ...EMPTY, loading: false, error: "AE not found in this tenant." });
          return;
        }
        const manager = ae.manager_user_id
          ? users.find((u) => String(u.id) === String(ae.manager_user_id)) || null
          : null;
        const accounts = allAccounts.filter((a) => String(a.owner_user_id) === aeId);
        const accountIds = accounts.map((a) => a.id);
        const [cps, allHunt] = await Promise.all([
          accountIds.length > 0 ? listCpStatusFor(accountIds) : Promise.resolve([]),
          listAllHunt(),
        ]);
        if (cancelled) return;
        const aeAccountSet = new Set(accountIds);
        const hunt = allHunt.filter((h) => aeAccountSet.has(h.account_id)).slice(0, 30);
        setState({
          loading: false,
          error: null,
          ae,
          manager,
          pipeline: pipeline.find((p) => String(p.ae_user_id) === aeId) || null,
          plan: plans.find((p) => String(p.ae_user_id) === aeId) || null,
          accounts,
          forecasts: forecasts.filter((f) => String(f.ae_user_id) === aeId),
          cpByAccount: summarizeCpStatus(cps),
          hunt,
        });
      } catch (err: any) {
        if (cancelled) return;
        setState({ ...EMPTY, loading: false, error: err?.message || "Failed to load" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [aeId]);

  const forecastByAccount = useMemo(
    () => new Map(state.forecasts.map((f) => [f.account_id, f])),
    [state.forecasts],
  );

  if (state.loading) {
    return (
      <Shell>
        <div style={{ padding: 40, textAlign: "center", color: "#8E8E94" }}>Loading…</div>
      </Shell>
    );
  }

  if (state.error || !state.ae) {
    return (
      <Shell>
        <div
          style={{
            padding: 16,
            background: "#FEF2F2",
            border: "1px solid #FCA5A5",
            borderRadius: 8,
            color: "#991B1B",
            fontSize: 13,
          }}
        >
          {state.error || "AE not found."}
        </div>
      </Shell>
    );
  }

  const ps = state.pipeline;
  const quota = Number(state.plan?.quota_annual || ps?.quota_annual || 0);
  const won = Number(ps?.won_value || 0);
  const attainmentPct = quota > 0 ? Math.round((won / quota) * 100) : 0;
  const totalWeighted = Number(ps?.weighted_pipeline || 0);

  return (
    <Shell>
      {/* Header */}
      <div
        style={{
          background: "#FFFFFF",
          border: "1px solid #E5E5E7",
          borderRadius: 12,
          padding: 24,
          marginBottom: 20,
        }}
      >
        <h1 style={{ fontSize: 22, fontWeight: 900, color: "#0B0B0F", margin: 0 }}>
          {state.ae.name || state.ae.full_name || state.ae.email}
        </h1>
        <div style={{ fontSize: 13, color: "#6B6B70", marginTop: 4 }}>
          {state.ae.email} · {(state.ae.role ?? "AE").toUpperCase()}
          {state.manager
            ? ` · Manager: ${state.manager.name || state.manager.email}`
            : <span style={{ color: "#C9C9CC", fontStyle: "italic" }}> · Manager hierarchy coming soon</span>
          }
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 28, marginTop: 18 }}>
          <Field label="Quota" value={quota > 0 ? fmtUSD(quota) : "—"} />
          <Field label="Won" value={fmtUSD(won)} />
          <Field
            label="Attainment"
            value={
              quota > 0 ? (
                <span
                  style={{
                    color:
                      attainmentPct >= 80 ? "#16A34A" : attainmentPct >= 50 ? "#CA8A04" : "#DC2626",
                  }}
                >
                  {attainmentPct}%
                </span>
              ) : (
                "—"
              )
            }
          />
          <Field label="Active opps" value={`${ps?.active_opps ?? state.accounts.length}`} />
          <Field label="Weighted forecast" value={fmtUSD(totalWeighted)} />
        </div>
      </div>

      {/* Pipeline by bucket */}
      <Section title="Pipeline by bucket">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 12,
          }}
        >
          {[
            { label: "COMMIT", value: Number(ps?.commit_value || 0) },
            { label: "PROBABLE", value: Number(ps?.probable_value || 0) },
            { label: "UPSIDE", value: Number(ps?.upside_value || 0) },
            { label: "OMITTED", value: Number(ps?.omitted_value || 0) },
          ].map((b) => (
            <div
              key={b.label}
              style={{
                background: "#FFFFFF",
                border: "1px solid #E5E5E7",
                borderRadius: 8,
                padding: 14,
                borderLeft: `4px solid ${bucketColor(b.label)}`,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  color: bucketColor(b.label),
                }}
              >
                {b.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0B0B0F", marginTop: 4 }}>
                {fmtUSD(b.value)}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Accounts table */}
      <Section title="Accounts" subtitle={`${state.accounts.length} owned`}>
        {state.accounts.length === 0 ? (
          <Empty text="No accounts assigned." />
        ) : (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#FAFAF7" }}>
                  {["Account", "Bucket", "Value", "Weighted", "CP", "Last activity"].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "10px 14px",
                        textAlign: "left",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: "#8E8E94",
                        borderBottom: "1px solid #EFEFEE",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.accounts.map((a) => {
                  const f = forecastByAccount.get(a.id);
                  const cp = state.cpByAccount?.get(a.id);
                  const lastActivity = cp?.lastUpdated || a.updated_at;
                  const days = daysSince(lastActivity);
                  const bucket = (f?.forecast_bucket || a.bucket || "").toUpperCase();
                  return (
                    <tr
                      key={a.id}
                      onClick={() => navigate(`/account/${a.id}`)}
                      style={{ cursor: "pointer", borderTop: "1px solid #FAFAF7" }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLElement).style.background = "#FAFAF7")
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLElement).style.background = "#FFFFFF")
                      }
                    >
                      <td style={{ padding: "10px 14px", color: "#0B0B0F", fontWeight: 600 }}>
                        {a.name}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        {bucket ? (
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: 4,
                              background: bucketColor(bucket) + "22",
                              color: bucketColor(bucket),
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.08em",
                            }}
                          >
                            {bucket}
                          </span>
                        ) : (
                          <span style={{ color: "#C9C9CC" }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        {fmtUSD(f?.deal_value ?? a.est_arr ?? 0)}
                      </td>
                      <td style={{ padding: "10px 14px" }}>{fmtUSD(f?.weighted_value ?? 0)}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span
                          style={{
                            color:
                              (cp?.green || 0) >= 7
                                ? "#16A34A"
                                : (cp?.green || 0) >= 4
                                  ? "#CA8A04"
                                  : "#8E8E94",
                            fontWeight: 700,
                          }}
                        >
                          {cp?.green || 0}
                        </span>
                        <span style={{ color: "#8E8E94" }}> / 10</span>
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <span
                          style={{
                            color: days > 14 ? "#DC2626" : "#525258",
                            fontWeight: days > 14 ? 700 : 400,
                          }}
                        >
                          {Number.isFinite(days) ? `${days}d` : "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Recent activity */}
      <Section title="Recent activity" subtitle={`Last ${state.hunt.length} hunt items`}>
        {state.hunt.length === 0 ? (
          <Empty text="No hunt activity recorded." />
        ) : (
          <div
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E5E7",
              borderRadius: 8,
              padding: 8,
            }}
          >
            {state.hunt.map((h) => (
              <div
                key={h.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "8px 12px",
                  borderBottom: "1px solid #FAFAF7",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: "#0B0B0F", fontWeight: 600 }}>
                    {h.label || h.item_key || "(unlabeled)"}
                  </div>
                  <div style={{ fontSize: 11, color: "#8E8E94" }}>
                    Status: {h.status}
                    {h.linked_cp ? ` · CP${h.linked_cp}` : ""}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "#8E8E94", whiteSpace: "nowrap" }}>
                  {daysSince(h.updated_at)}d ago
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <OperatorShell breadcrumbs={[{ label: "Reporting", href: "/reporting" }, { label: "AE Detail" }]}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        {children}
      </div>
    </OperatorShell>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#8E8E94",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#0B0B0F" }}>{value}</div>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 20 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: "#0B0B0F" }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "#8E8E94" }}>{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px dashed #C9C9CC",
        borderRadius: 8,
        padding: 16,
        color: "#8E8E94",
        fontSize: 13,
        textAlign: "center",
      }}
    >
      {text}
    </div>
  );
}
